/**
 * CampoLog — IndexedDB offline database layer.
 * Stores animals, events, and pending operations locally for field use.
 */

const DB_NAME = 'campolog_offline';
const DB_VERSION = 2;

const STORES = {
  animales: 'animales',
  pesajes: 'pesajes',
  servicios: 'servicios',
  pariciones: 'pariciones',
  controles: 'controles_machos',
  pendingOps: 'pending_operations',
  meta: 'meta',
};

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORES.animales)) {
        const store = db.createObjectStore(STORES.animales, { keyPath: 'id' });
        store.createIndex('VID', 'VID', { unique: false });
        store.createIndex('IDE', 'IDE', { unique: false });
        store.createIndex('establecimiento_id', 'establecimiento_id', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORES.pesajes)) db.createObjectStore(STORES.pesajes, { keyPath: 'id' });
      if (!db.objectStoreNames.contains(STORES.servicios)) db.createObjectStore(STORES.servicios, { keyPath: 'id' });
      if (!db.objectStoreNames.contains(STORES.pariciones)) db.createObjectStore(STORES.pariciones, { keyPath: 'id' });
      if (!db.objectStoreNames.contains(STORES.controles)) db.createObjectStore(STORES.controles, { keyPath: 'id' });
      if (!db.objectStoreNames.contains(STORES.pendingOps)) {
        db.createObjectStore(STORES.pendingOps, { keyPath: 'id', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(STORES.meta)) db.createObjectStore(STORES.meta, { keyPath: 'key' });
    };
  });
}

// ─── Generic CRUD ────────────────────────────────────────────────────

async function putAll(storeName, items) {
  const db = await openDB();
  const tx = db.transaction(storeName, 'readwrite');
  const store = tx.objectStore(storeName);
  for (const item of items) {
    store.put(item);
  }
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve(items.length);
    tx.onerror = () => reject(tx.error);
  });
}

async function getAll(storeName) {
  const db = await openDB();
  const tx = db.transaction(storeName, 'readonly');
  const store = tx.objectStore(storeName);
  return new Promise((resolve, reject) => {
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function getByKey(storeName, key) {
  const db = await openDB();
  const tx = db.transaction(storeName, 'readonly');
  const store = tx.objectStore(storeName);
  return new Promise((resolve, reject) => {
    const req = store.get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function putOne(storeName, item) {
  const db = await openDB();
  const tx = db.transaction(storeName, 'readwrite');
  tx.objectStore(storeName).put(item);
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function clearStore(storeName) {
  const db = await openDB();
  const tx = db.transaction(storeName, 'readwrite');
  tx.objectStore(storeName).clear();
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ─── Animals ─────────────────────────────────────────────────────────

export async function cacheAnimales(animales) {
  await clearStore(STORES.animales);
  await putAll(STORES.animales, animales);
  await putOne(STORES.meta, { key: 'animales_cached_at', value: new Date().toISOString() });
  console.log(`[OfflineDB] Cached ${animales.length} animals`);
}

export async function getCachedAnimales(establecimientoId) {
  const all = await getAll(STORES.animales);
  if (establecimientoId) return all.filter(a => a.establecimiento_id === establecimientoId);
  return all;
}

export async function searchAnimalLocal(query, establecimientoId) {
  const animales = await getCachedAnimales(establecimientoId);
  const q = query.toLowerCase();
  return animales.filter(a =>
    (a.VID && a.VID.toLowerCase().includes(q)) ||
    (a.IDE && a.IDE.toLowerCase().includes(q)) ||
    (a.raza && a.raza.toLowerCase().includes(q))
  );
}

export async function getAnimalById(id) {
  return getByKey(STORES.animales, id);
}

// ─── Pesajes cache ───────────────────────────────────────────────────

export async function cachePesajes(pesajes) {
  await clearStore(STORES.pesajes);
  await putAll(STORES.pesajes, pesajes);
  await putOne(STORES.meta, { key: 'pesajes_cached_at', value: new Date().toISOString() });
  console.log(`[OfflineDB] Cached ${pesajes.length} pesajes`);
}

export async function getCachedPesajes() {
  return getAll(STORES.pesajes);
}

export async function getPesajesByAnimal(animalId) {
  const all = await getAll(STORES.pesajes);
  return all.filter(p => p.animal_id === animalId).sort((a, b) => (b.fecha || '').localeCompare(a.fecha || ''));
}

// ─── Servicios cache ─────────────────────────────────────────────────

export async function cacheServicios(servicios) {
  await clearStore(STORES.servicios);
  await putAll(STORES.servicios, servicios);
  await putOne(STORES.meta, { key: 'servicios_cached_at', value: new Date().toISOString() });
  console.log(`[OfflineDB] Cached ${servicios.length} servicios`);
}

export async function getCachedServicios() {
  return getAll(STORES.servicios);
}

// ─── Pariciones cache ────────────────────────────────────────────────

export async function cachePariciones(pariciones) {
  await clearStore(STORES.pariciones);
  await putAll(STORES.pariciones, pariciones);
  await putOne(STORES.meta, { key: 'pariciones_cached_at', value: new Date().toISOString() });
  console.log(`[OfflineDB] Cached ${pariciones.length} pariciones`);
}

export async function getCachedPariciones() {
  return getAll(STORES.pariciones);
}

// ─── Pending operations (offline queue) ──────────────────────────────

export async function addPendingOp(operation) {
  const op = {
    ...operation,
    id: `op_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    timestamp: new Date().toISOString(),
    synced: false,
    retries: 0,
  };
  await putOne(STORES.pendingOps, op);
  return op;
}

export async function getPendingOps() {
  return getAll(STORES.pendingOps);
}

export async function removePendingOp(opId) {
  const db = await openDB();
  const tx = db.transaction(STORES.pendingOps, 'readwrite');
  tx.objectStore(STORES.pendingOps).delete(opId);
  return new Promise((resolve) => { tx.oncomplete = () => resolve(); });
}

export async function clearPendingOps() {
  await clearStore(STORES.pendingOps);
}

// ─── Sync engine ─────────────────────────────────────────────────────

const ID_FIELDS = ['animal_id', 'hembra_id', 'macho_id', 'madre_id', 'toro_id', 'padre_id', 'ternero_id'];
const isOfflineId = (v) => typeof v === 'string' && v.startsWith('offline_');

function remapIds(data, idMap) {
  if (!data || typeof data !== 'object') return data;
  const out = { ...data };
  for (const f of ID_FIELDS) {
    if (isOfflineId(out[f]) && idMap[out[f]]) out[f] = idMap[out[f]];
  }
  return out;
}

let _syncInFlight = null;

export async function syncPendingOps(apiInstance) {
  if (_syncInFlight) return _syncInFlight;
  _syncInFlight = _syncPendingOpsImpl(apiInstance).finally(() => { _syncInFlight = null; });
  return _syncInFlight;
}

async function _syncPendingOpsImpl(apiInstance) {
  if (!navigator.onLine) return { synced: 0, failed: 0, pending: 0 };

  const ops = (await getPendingOps()).filter(op => !op.failed);
  if (!ops.length) return { synced: 0, failed: 0, pending: (await getPendingOps()).length };

  let synced = 0;
  let failed = 0;
  const idMap = {};

  for (const op of ops) {
    const data = remapIds(op.data, idMap);
    const dependsOnUnsynced = ID_FIELDS.some(f => isOfflineId(data?.[f]));
    if (dependsOnUnsynced) { failed++; continue; }
    try {
      let res;
      if (op.method === 'POST') res = await apiInstance.post(op.url, data);
      else if (op.method === 'PUT') res = await apiInstance.put(op.url, data);
      else if (op.method === 'DELETE') res = await apiInstance.delete(op.url);

      if (data?.offline_id && res?.data?.animal_id) {
        idMap[data.offline_id] = res.data.animal_id;
        const local = await getByKey(STORES.animales, data.offline_id);
        if (local) {
          const db = await openDB();
          const tx = db.transaction(STORES.animales, 'readwrite');
          tx.objectStore(STORES.animales).delete(data.offline_id);
          tx.objectStore(STORES.animales).put({ ...local, id: res.data.animal_id });
        }
      }
      await removePendingOp(op.id);
      synced++;
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // Sin respuesta del servidor (red caida) → no cuenta como intento, se reintenta luego
      if (!status || status >= 500) { failed++; continue; }
      if (status === 401) { failed++; break; }
      op.retries = (op.retries || 0) + 1;
      op.lastError = typeof detail === 'string' ? detail : JSON.stringify(detail || err.message);
      // Duplicado (ya existe): el dato ya esta en el servidor, se considera sincronizado
      if (status === 400 && /ya existe/i.test(op.lastError)) {
        await removePendingOp(op.id);
        synced++;
        continue;
      }
      if (op.retries >= 5) op.failed = true; // se conserva para revision manual, no se descarta
      await putOne(STORES.pendingOps, op);
      failed++;
    }
  }

  return { synced, failed, pending: (await getPendingOps()).length };
}

// ─── Cache animals locally (also add new ones from offline ops) ──────

export async function addAnimalToCache(animal) {
  await putOne(STORES.animales, animal);
}

// ─── Meta ────────────────────────────────────────────────────────────

export async function getLastCacheTime() {
  const meta = await getByKey(STORES.meta, 'animales_cached_at');
  return meta?.value || null;
}

export async function getCacheStats() {
  const animales = await getAll(STORES.animales);
  const pesajes = await getAll(STORES.pesajes);
  const servicios = await getAll(STORES.servicios);
  const pariciones = await getAll(STORES.pariciones);
  const pending = await getPendingOps();
  const lastCache = await getLastCacheTime();
  const pesajesTime = await getByKey(STORES.meta, 'pesajes_cached_at');
  const serviciosTime = await getByKey(STORES.meta, 'servicios_cached_at');
  const paricionesTime = await getByKey(STORES.meta, 'pariciones_cached_at');
  return {
    animalesCached: animales.length,
    pesajesCached: pesajes.length,
    serviciosCached: servicios.length,
    paricionesCached: pariciones.length,
    pendingOps: pending.length,
    lastCacheTime: lastCache,
    pesajesCachedAt: pesajesTime?.value || null,
    serviciosCachedAt: serviciosTime?.value || null,
    paricionesCachedAt: paricionesTime?.value || null,
  };
}

export async function clearAllCache() {
  const db = await openDB();
  const tx = db.transaction([STORES.animales, STORES.pesajes, STORES.servicios, STORES.pariciones, STORES.controles, STORES.meta], 'readwrite');
  tx.objectStore(STORES.animales).clear();
  tx.objectStore(STORES.pesajes).clear();
  tx.objectStore(STORES.servicios).clear();
  tx.objectStore(STORES.pariciones).clear();
  tx.objectStore(STORES.controles).clear();
  tx.objectStore(STORES.meta).clear();
  return new Promise((resolve, reject) => {
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
  });
}
